import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { UserPlus } from "lucide-react";
import { BUILT_IN_USER_TYPE_IDS, type UserDto } from "@platform/core";
import {
  Badge,
  Button,
  DataGrid,
  EMPTY_FILTER_MODEL,
  EmptyState,
  type GridColDef,
  type GridFilterModel,
  type GridSortModel,
} from "@platform/ui";
import type { ApiClient } from "../../api/client";
import { useApi, useConsole } from "../../context";
import { useT, type TFunc } from "../../i18n";
import { UserTypeBadge, useGroups, useUserTypes } from "../../lib/access";
import { formatDate, formatDateTime, initials } from "../../lib/format";
import { PAGE_SIZES, useCursorPagination, useGridText } from "../../lib/grid";
import { ErrorBanner, PageCard, ReloadButton } from "../../layout/Page";
import { AddUserDialog } from "./AddUserDialog";

export function StatusBadge({ status, t }: { status: UserDto["status"]; t: TFunc }) {
  return (
    <Badge dot tone={status === "active" ? "success" : status === "banned" ? "danger" : "warning"}>
      {t(`status.${status}`)}
    </Badge>
  );
}

export function Avatar({
  user,
  size = "size-8",
}: {
  user: Pick<UserDto, "displayName" | "avatarUrl">;
  size?: string;
}) {
  return user.avatarUrl ? (
    <img src={user.avatarUrl} alt="" className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <span
      aria-hidden
      className={`${size} inline-flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-blue-200 text-xs font-semibold text-primary-dark ring-2 ring-paper`}
    >
      {initials(user.displayName)}
    </span>
  );
}

const STATUSES = ["active", "pending", "banned"] as const;

type UserQuery = Parameters<ApiClient["users"]["list"]>[0];

/** Grid filter + sort models → query parameters the users API understands (server-side). */
export function usersQuery(filter: GridFilterModel, sort: GridSortModel): UserQuery {
  const q: UserQuery = {};
  const text = filter.quickFilterValues.join(" ").trim();
  if (text) q.q = text;
  for (const i of filter.items) {
    if (i.value === undefined || i.value === "") continue;
    if (i.field === "status") q.status = String(i.value);
    if (i.field === "roles") q.role = String(i.value);
    if (i.field === "mfaEnabled") q.mfa = String(i.value);
    if (i.field === "userType") q.type = String(i.value);
    if (i.field === "groups") q.group = String(i.value);
  }
  const s = sort[0];
  if (s) {
    q.sort = s.field === "email" ? "email" : s.field === "displayName" ? "displayName" : "createdAt";
    q.order = s.sort;
  }
  return q;
}

/** Every page for the current query (bounded) — CSV export. */
async function fetchAllUsers(api: ApiClient, q: UserQuery, max = 5000): Promise<UserDto[]> {
  const out: UserDto[] = [];
  let cursor: string | undefined;
  do {
    const page = await api.users.list({ ...q, limit: 100, ...(cursor ? { cursor } : {}) });
    out.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor && out.length < max);
  return out;
}

export function UsersPage() {
  const { t, locale } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const navigate = useNavigate();
  const text = useGridText();
  const [filterModel, setFilterModel] = useState<GridFilterModel>(EMPTY_FILTER_MODEL);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [adding, setAdding] = useState(false);
  const query = usersQuery(filterModel, sortModel);
  const paging = useCursorPagination("users", JSON.stringify(query));

  const roles = useQuery({ queryKey: ["roles"], queryFn: api.roles.list, enabled: can("roles:read") });
  const userTypes = useUserTypes();
  const groups = useGroups();
  const typeIds = useMemo(
    () => userTypes.data?.items.map((x) => x.id) ?? BUILT_IN_USER_TYPE_IDS,
    [userTypes.data],
  );
  const users = useQuery({
    queryKey: ["users", query, paging.model.pageSize, paging.cursor],
    queryFn: () =>
      api.users.list({
        ...query,
        limit: paging.model.pageSize,
        ...(paging.cursor ? { cursor: paging.cursor } : {}),
      }),
    placeholderData: keepPreviousData,
  });
  const open = (id: string) => void navigate({ to: "/users/$id", params: { id } });

  const columns = useMemo<GridColDef<UserDto>[]>(
    () => [
      {
        field: "email",
        headerName: t("users.col.identifier"),
        minWidth: 260,
        filterable: false,
        hideable: false,
        renderCell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar user={row} />
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-medium">
                <span className="truncate">{row.userCode === "root" ? "root" : row.email}</span>
                {row.userCode === "root" && <Badge tone="warning">{t("users.rootBadge")}</Badge>}
              </p>
              <p className="truncate text-xs text-ink-2">
                {[
                  row.userCode && row.userCode !== "root" ? row.userCode : null,
                  row.username ? `@${row.username}` : null,
                  row.displayName,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          </div>
        ),
      },
      {
        field: "displayName",
        headerName: t("user.displayName"),
        minWidth: 160,
        filterable: false,
        hiddenByDefault: true,
      },
      {
        field: "linkedProviders",
        headerName: t("users.col.providers"),
        width: 150,
        sortable: false,
        filterable: false,
        valueFormatter: (v) =>
          (v as string[]).length ? (v as string[]).join(", ") : t("users.provider.password"),
        renderCell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.linkedProviders.length === 0 ? (
              <Badge>{t("users.provider.password")}</Badge>
            ) : (
              row.linkedProviders.map((p) => <Badge key={p}>{p}</Badge>)
            )}
          </div>
        ),
      },
      {
        field: "createdAt",
        headerName: t("users.col.created"),
        type: "dateTime",
        width: 140,
        filterable: false,
        valueFormatter: (v) => formatDate(String(v), locale, config.timeZone),
      },
      {
        field: "lastSignInAt",
        headerName: t("users.col.lastSignIn"),
        type: "dateTime",
        width: 170,
        sortable: false,
        filterable: false,
        valueFormatter: (v) => (v ? formatDateTime(String(v), locale, config.timeZone) : t("common.never")),
      },
      {
        field: "status",
        headerName: t("users.col.status"),
        type: "singleSelect",
        width: 130,
        sortable: false,
        filterOperators: ["is"],
        valueOptions: STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) })),
        renderCell: ({ row }) => <StatusBadge status={row.status} t={t} />,
      },
      {
        field: "mfaEnabled",
        headerName: t("users.filter.mfa"),
        type: "boolean",
        width: 130,
        sortable: false,
        valueFormatter: (v) => (v ? t("users.filter.mfaOn") : t("users.filter.mfaOff")),
        renderCell: ({ row }) => (
          <Badge dot tone={row.mfaEnabled ? "success" : "neutral"}>
            {row.mfaEnabled ? t("users.filter.mfaOn") : t("users.filter.mfaOff")}
          </Badge>
        ),
      },
      {
        field: "roles",
        headerName: t("users.col.roles"),
        type: "singleSelect",
        width: 170,
        sortable: false,
        filterable: can("roles:read"),
        filterOperators: ["is"],
        valueOptions: (roles.data?.items ?? []).map((r) => ({ value: r.id, label: r.name })),
        renderCell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.roles.map((r) => (
              <Badge key={r} tone="info">
                {r}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        field: "userType",
        headerName: t("access.userType"),
        type: "singleSelect",
        width: 130,
        sortable: false,
        filterOperators: ["is"],
        valueOptions: typeIds.map((v) => ({ value: v, label: v })),
        renderCell: ({ row }) => <UserTypeBadge type={row.userType} />,
      },
      {
        field: "groups",
        headerName: t("access.groups"),
        type: "singleSelect",
        width: 200,
        sortable: false,
        hiddenByDefault: true,
        filterable: can("groups:read"),
        filterOperators: ["is"],
        valueOptions: (groups.data?.items ?? []).map((g) => ({ value: g.id, label: g.name })),
        valueFormatter: (v) => (v as string[]).join(", "),
        renderCell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.groups.map((g) => (
              <Badge key={g}>{g}</Badge>
            ))}
          </div>
        ),
      },
      {
        field: "id",
        headerName: t("user.id"),
        width: 300,
        hiddenByDefault: true,
        sortable: false,
        filterable: false,
        cellClassName: "font-mono text-xs",
      },
    ],
    [t, locale, config.timeZone, can, roles.data, groups.data, typeIds],
  );

  return (
    <PageCard>
      {users.isError && <ErrorBanner error={users.error} onRetry={() => void users.refetch()} />}
      <DataGrid
        id="users"
        label={t("users.table")}
        localeText={text}
        mode="server"
        rows={users.data?.items ?? []}
        columns={columns}
        loading={users.isFetching}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        paginationModel={paging.model}
        onPaginationModelChange={(m) => paging.onChange(m, users.data?.nextCursor)}
        pageSizeOptions={PAGE_SIZES}
        {...(users.data?.total !== undefined ? { rowCount: users.data.total } : {})}
        hasNextPage={Boolean(users.data?.nextCursor)}
        disableFilterOr
        disableLastPage
        checkboxSelection
        onRowClick={(u) => open(u.id)}
        getRowAttributes={(u) => ({ "data-testid": "user-row", "data-email": u.email })}
        onExport={() => fetchAllUsers(api, query)}
        exportFileName="users"
        noRowsContent={
          <EmptyState
            title={t("users.empty")}
            action={
              can("users:write") ? (
                <Button onClick={() => setAdding(true)}>{t("users.add")}</Button>
              ) : undefined
            }
          />
        }
        toolbarActions={
          <>
            <ReloadButton onClick={() => void users.refetch()} />
            {can("users:write") && (
              <Button data-testid="add-user-button" onClick={() => setAdding(true)}>
                <UserPlus className="size-4" aria-hidden />
                {t("users.add")}
              </Button>
            )}
          </>
        }
      />
      <AddUserDialog
        open={adding}
        onOpenChange={setAdding}
        roles={roles.data?.items ?? []}
        userTypes={typeIds}
        groups={groups.data?.items ?? []}
        onCreated={open}
      />
    </PageCard>
  );
}
